import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  ChevronDown,
  BarChart3,
  Mic,
  PenTool,
  FileVideo,
  FileAudio,
  ThumbsUp,
  Zap,
  Target,
  MessageSquareQuote,
  BookOpen,
  TrendingUp,
} from "lucide-react";
import type { VoiceStats, VoiceAnalysis } from "@workspace/api-client-react";

function ConfidenceBadge({ level }: { level: string }) {
  const config = {
    Low: { color: "text-amber-400", bg: "bg-amber-400/10 border-amber-400/30" },
    Building: { color: "text-primary", bg: "bg-primary/10 border-primary/30" },
    Strong: { color: "text-emerald-400", bg: "bg-emerald-400/10 border-emerald-400/30" },
  }[level] ?? { color: "text-muted-foreground", bg: "bg-secondary border-border" };

  const desc = {
    Low: "Just getting started — add more samples",
    Building: "Good foundation — getting better",
    Strong: "AI has a solid read on your voice",
  }[level] ?? "";

  return (
    <div className={`inline-flex flex-col items-start gap-0.5`}>
      <span className={`px-2.5 py-1 rounded-full text-xs font-bold border ${config.bg} ${config.color}`}>
        {level}
      </span>
      {desc && <span className="text-[10px] text-muted-foreground ml-1">{desc}</span>}
    </div>
  );
}

function StatRow({ icon: Icon, label, count, color }: { icon: React.ElementType; label: string; count: number; color: string }) {
  if (count === 0) return null;
  return (
    <div className="flex items-center justify-between text-sm">
      <div className="flex items-center gap-2 text-muted-foreground">
        <Icon className={`w-3.5 h-3.5 ${color}`} />
        <span>{label}</span>
      </div>
      <span className="font-medium text-foreground tabular-nums">{count}</span>
    </div>
  );
}

interface VoiceStatsCardProps {
  stats: VoiceStats;
  analysis?: VoiceAnalysis | null;
  sampleCount: number;
}

export function VoiceStatsCard({ stats, analysis, sampleCount }: VoiceStatsCardProps) {
  const [open, setOpen] = useState(false);

  if (sampleCount === 0) return null;

  const totalWords = stats.sampleWordCounts.reduce((a: number, b: number) => a + b, 0);
  const avgWords = sampleCount > 0 ? Math.round(totalWords / sampleCount) : 0;

  const traits = analysis
    ? [
        { label: "Tone", value: analysis.tone },
        { label: "Hook style", value: analysis.hookStyle },
        { label: "CTA pattern", value: analysis.ctaPattern || null },
        { label: "Sentence length", value: analysis.avgSentenceLength ? `~${Math.round(analysis.avgSentenceLength)} words` : null },
        { label: "Cadence", value: analysis.cadence },
        { label: "Story pattern", value: analysis.storyPattern },
      ].filter((t) => t.value)
    : [];

  return (
    <motion.div
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      className="rounded-2xl border border-primary/20 bg-card overflow-hidden shadow-[0_0_18px_hsl(188_90%_42%_/_0.07)]"
    >
      <button
        onClick={() => setOpen((v) => !v)}
        className="w-full flex items-center justify-between p-4 hover:bg-secondary/20 transition-colors"
      >
        <div className="flex items-center gap-2.5">
          <div className="w-8 h-8 rounded-lg bg-primary/15 flex items-center justify-center shadow-[0_0_12px_hsl(188_90%_42%_/_0.2)]">
            <BarChart3 className="w-4 h-4 text-primary" />
          </div>
          <div className="text-left">
            <p className="text-sm font-semibold text-foreground">What We've Learned</p>
            <p className="text-xs text-muted-foreground">{sampleCount} sample{sampleCount !== 1 ? "s" : ""} · {stats.confidenceLevel} confidence</p>
          </div>
        </div>
        <ChevronDown className={`w-4 h-4 text-muted-foreground transition-transform ${open ? "rotate-180" : ""}`} />
      </button>

      <AnimatePresence initial={false}>
        {open && (
          <motion.div
            key="content"
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.2, ease: "easeInOut" }}
            className="overflow-hidden"
          >
            <div className="px-4 pb-4 space-y-5 border-t border-border pt-4">
              <div>
                <p className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground mb-2">Training Confidence</p>
                <ConfidenceBadge level={stats.confidenceLevel} />
              </div>

              <div>
                <p className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground mb-2">Source Breakdown</p>
                <div className="space-y-2">
                  <StatRow icon={PenTool} label="Text samples" count={stats.textSampleCount} color="text-primary" />
                  <StatRow icon={Mic} label="Live recordings" count={stats.liveAudioCount} color="text-emerald-400" />
                  <StatRow icon={FileAudio} label="Uploaded audio" count={stats.uploadedAudioCount} color="text-blue-400" />
                  <StatRow icon={FileVideo} label="Uploaded video" count={stats.uploadedVideoCount} color="text-violet-400" />
                  <StatRow icon={ThumbsUp} label="From feedback" count={stats.feedbackCount} color="text-amber-400" />
                </div>
                {avgWords > 0 && (
                  <p className="text-[10px] text-muted-foreground mt-2">
                    Avg {avgWords} words per sample · {totalWords} total words
                  </p>
                )}
              </div>

              {traits.length > 0 && (
                <div>
                  <p className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground mb-2">Inferred Writing Traits</p>
                  <div className="space-y-1.5">
                    {traits.map(({ label, value }) => (
                      <div key={label} className="flex justify-between items-start gap-2 text-sm">
                        <span className="text-muted-foreground text-xs">{label}</span>
                        <span className="font-medium text-xs text-right max-w-[55%] leading-snug capitalize">{value}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {analysis?.powerWords && analysis.powerWords.length > 0 && (
                <div>
                  <p className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground mb-2 flex items-center gap-1">
                    <Zap className="w-3 h-3" /> Power Words
                  </p>
                  <div className="flex flex-wrap gap-1.5">
                    {analysis.powerWords.slice(0, 8).map((word: string, i: number) => (
                      <span key={i} className="px-2 py-0.5 bg-amber-500/10 text-amber-400 text-xs font-medium rounded-md">
                        {word}
                      </span>
                    ))}
                  </div>
                </div>
              )}

              {analysis?.signaturePhrases && analysis.signaturePhrases.length > 0 && (
                <div>
                  <p className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground mb-2 flex items-center gap-1">
                    <MessageSquareQuote className="w-3 h-3" /> Signature Phrases
                  </p>
                  <div className="flex flex-wrap gap-1.5">
                    {analysis.signaturePhrases.slice(0, 5).map((phrase: string, i: number) => (
                      <span key={i} className="px-2 py-0.5 bg-secondary text-xs rounded-md">"{phrase}"</span>
                    ))}
                  </div>
                </div>
              )}

              {analysis?.hookTaxonomy && (
                <div>
                  <p className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground mb-1 flex items-center gap-1">
                    <TrendingUp className="w-3 h-3" /> Hook Style
                  </p>
                  <p className="text-xs text-foreground/80">{analysis.hookTaxonomy}</p>
                </div>
              )}

              {analysis?.ctaPattern && (
                <div>
                  <p className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground mb-1 flex items-center gap-1">
                    <Target className="w-3 h-3" /> CTA Pattern
                  </p>
                  <p className="text-xs text-foreground/80">{analysis.ctaPattern}</p>
                </div>
              )}

              {stats.firstTrainedAt && (
                <div className="text-[10px] text-muted-foreground border-t border-border pt-3 space-y-0.5">
                  <p>First trained: {new Date(stats.firstTrainedAt).toLocaleDateString()}</p>
                  {stats.lastTrainedAt && stats.lastTrainedAt !== stats.firstTrainedAt && (
                    <p>Last updated: {new Date(stats.lastTrainedAt).toLocaleDateString()}</p>
                  )}
                </div>
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
}
